import { Button, Group, Modal, Stack, Stepper, Title } from "@mantine/core"
import { useForm } from "@mantine/form"
import {
  IconArrowLeft,
  IconArrowRight,
  IconDeviceFloppy,
} from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import { useState } from "react"

import {
  DigestConfig,
  digestOneTimeConfigSchemaStep2,
  digestOneTimeConfigSchemaStep3,
  DigestResponses,
} from "@costory/types/endpoints/digest"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { DigestConfigStep2 } from "@costory/front/pages/DigestV2/v3/Step2Thresholds"
import { DigestConfigStep3 } from "@costory/front/pages/DigestV2/v3/Step3Tree"
import {
  useEditDigestConfigMutation,
  useGetDigestConfigQuery,
} from "@costory/front/queries/digest"

const _DigestConfigStepper = ({
  defaultValues,
  onSave,
}: {
  defaultValues: DigestResponses.DigestConfig
  onSave: (values: DigestConfig) => void
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0)

  const form = useForm<DigestConfig>({
    initialValues: {
      columns: defaultValues?.columns ?? [],
      whereClause: defaultValues?.whereClause ?? {
        combinator: "and",
        rules: [],
      },
      minDiffThreshold: defaultValues?.minDiffThreshold ?? 0,
      relativeDiffThreshold: defaultValues?.relativeDiffThreshold ?? 0,
      costColumn: defaultValues?.costColumn ?? "cost",
    },
    validate: zodResolver(
      currentStep === 0
        ? digestOneTimeConfigSchemaStep2
        : digestOneTimeConfigSchemaStep3,
    ),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  })
  const steps = [
    {
      name: "Configure Analysis",
      component: <DigestConfigStep2 key="step2" form={form} />,
      description: "Configure the analysis for the digest",
    },
    {
      name: "Configure Tree",
      component: <DigestConfigStep3 key="step3" form={form} />,
      description: "Configure the tree for the digest",
    },
  ]
  const nextStep = () => {
    if (form.isValid()) {
      setCurrentStep(
        currentStep < steps.length - 1 ? currentStep + 1 : currentStep,
      )
    }
  }
  const previousStep = () =>
    setCurrentStep(currentStep > 0 ? currentStep - 1 : currentStep)

  return (
    <form onSubmit={form.onSubmit(onSave)}>
      <Stack justify="space-between" h="100%">
        <Title order={4}>Request New Digest</Title>
        <Stepper active={currentStep} onStepClick={setCurrentStep}>
          {steps.map((step, index) => (
            <Stepper.Step
              key={index}
              label={step.name}
              description={step.description}
            >
              {step.component}
            </Stepper.Step>
          ))}
        </Stepper>
        <Group justify="flex-end">
          <Button
            onClick={previousStep}
            leftSection={<IconArrowLeft />}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          <Button
            onClick={
              currentStep === steps.length - 1
                ? () => form.onSubmit(onSave)
                : nextStep
            }
            rightSection={
              currentStep === steps.length - 1 ? (
                <IconDeviceFloppy />
              ) : (
                <IconArrowRight />
              )
            }
          >
            {currentStep === steps.length - 1 ? "Save" : "Next"}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

export const DigestConfigStepper = ({
  opened,
  onClose,
}: {
  opened: boolean
  onClose: () => void
}) => {
  const queryDefaultDigestConfig = useGetDigestConfigQuery()
  const { mutateAsync: editConfig } = useEditDigestConfigMutation({
    closeModal: () => {},
  })
  return (
    <Modal size="100vw" opened={opened} onClose={onClose}>
      <QueryWrapper query={queryDefaultDigestConfig}>
        {({ data }) => (
          <_DigestConfigStepper defaultValues={data} onSave={editConfig} />
        )}
      </QueryWrapper>
    </Modal>
  )
}
